import React from 'react';
import { ProductSalesSummaryItem } from '../../../services/productSalesSummaryApi';
import i18n from '../../../i18n';

// ============================================
// 80MM FİŞ YAZDIRMA SERVİSİ - GÜN SONU RAPORU
// ============================================

export interface EndOfDayPrintData {
  // Başlık bilgileri
  branchName: string;
  cashierNames: string[]; // Birden fazla kasiyer olabilir
  reportStartDate: string;
  reportEndDate: string;
  printDate: string; // Alındığı tarih
  
  // Ürün satış özeti
  productSales: ProductSalesSummaryItem[];
  
  // Ciro
  totalRevenue: number;
  
  // Müşteri sayısı (tüm tab'lardaki fiş toplamı)
  customerCount: number;
  
  // Ödeme detayları (birleştirilmiş)
  paymentSummary: {
    cashTotal: number;        // Tüm kasalar toplamı
    posTotal: number;         // Tüm POS'lar toplamı
    onlinePayments: Array<{   // Online platformlar (ayrı ayrı)
      name: string;
      amount: number;
    }>;
    otherPayments: Array<{    // Diğer ödemeler (Multinet, Pluxee vb.)
      name: string;
      amount: number;
    }>;
  };
  
  // Genel toplam ve iptal
  grandTotal: number;
  cancelledTotal: number;
  
  // Yemek sitesi sipariş detayları
  // total       = net toplam (indirim sonrası ödenen, grandTotal)
  // originalTotal = brüt toplam (indirim öncesi, EndOfDayReport'taki "İndirimsiz Toplam"
  //                kolonuyla aynı kaynak — platform-spesifik computeOriginal sonucu).
  //                Eski siparişlerde dolmadıysa otomatik olarak total'e düşer.
  platformOrders?: {
    getir: { count: number; total: number; originalTotal: number };
    trendyol: { count: number; total: number; originalTotal: number };
    yemeksepeti: { count: number; total: number; originalTotal: number };
    migros: { count: number; total: number; originalTotal: number };
  };
  // Diğer ödeme yöntemleri toplamları (Multinet, Pluxee, Metropol vb.)
  otherMethodTotals?: Array<{
    name: string;
    amount: number;
  }>;
  // POS iade bilgileri
  posRefundTotal?: number;
  posRefundCount?: number;
  // PYK ön ödemeli (kart) satış bilgileri — bilgi amaçlı; kasa/banka toplamına dahil değildir
  prepaidTotal?: number;
  prepaidCount?: number;
  // İptal edilen adisyonlar (restoran + hızlı satış)
  cancelledOrders?: Array<{
    tableName: string;
    orderNumber: string;
    amount: number;
    waiterName?: string;
    notes?: string;
    closedAt?: string;
    source?: 'restaurant' | 'quickSale';
  }>;
  // Ödenmemiş platform siparişleri
  unpaidPlatformOrders?: {
    package: { count: number; total: number };
    getir: { count: number; total: number };
    trendyol: { count: number; total: number };
    yemeksepeti: { count: number; total: number };
    migros: { count: number; total: number };
  };
}

// Para formatı
const formatCurrency = (amount: number): string => {
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'tr-TR';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Tarih formatı
const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const locale = i18n.language?.startsWith('en') ? 'en-US' : 'tr-TR';
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return dateString;
  }
};

// Metin kırpma (satır uzunluğuna göre)
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 2) + '..';
};

// 80mm fiş için HTML şablonu oluştur
export const generateEndOfDayReceiptHTML = (data: EndOfDayPrintData): string => {
  const t = (key: string, fallback: string, options?: Record<string, any>) =>
    i18n.t(key, { ns: 'salesSystem', defaultValue: fallback, ...options });

  const {
    branchName,
    cashierNames,
    reportStartDate,
    reportEndDate,
    printDate,
    productSales,
    totalRevenue,
    customerCount,
    paymentSummary,
    grandTotal,
    cancelledTotal,
    platformOrders,
    otherMethodTotals,
    unpaidPlatformOrders,
  } = data;

  // Ürün ve indirim kalemlerini ayır
  const regularProductsRaw = productSales.filter(p => p.TotalAmount >= 0);
  const discountItems = productSales.filter(p => p.TotalAmount < 0);

  // ✅ Aynı isimli ürünleri TEK kalemde topla (adet + tutar birleştirilir),
  // ardından ürün adına göre ALFABETİK sırala. Birim fiyat, birleşik kalemde
  // farklı fiyatlar olabileceği için tutar/adet ağırlıklı ortalamasıyla yeniden
  // hesaplanır (BF × Adet ≈ Toplam tutarlılığı korunur).
  const mergedMap = new Map<string, ProductSalesSummaryItem>();
  for (const p of regularProductsRaw) {
    const displayName = (p.ProductName || '').trim();
    const key = displayName.toLocaleLowerCase('tr-TR');
    const existing = mergedMap.get(key);
    if (existing) {
      existing.TotalQuantity += p.TotalQuantity || 0;
      existing.TotalAmount += p.TotalAmount || 0;
    } else {
      mergedMap.set(key, { ...p, ProductName: displayName });
    }
  }
  const regularProducts = Array.from(mergedMap.values())
    .map(p => ({
      ...p,
      UnitPrice: p.TotalQuantity !== 0 ? p.TotalAmount / p.TotalQuantity : p.UnitPrice,
    }))
    .sort((a, b) => a.ProductName.localeCompare(b.ProductName, 'tr-TR'));

  const regularRows = regularProducts.map(product => {
    const productName = truncateText(product.ProductName, 18);
    const bf = formatCurrency(product.UnitPrice);
    const mkt = product.TotalQuantity.toString();
    const total = formatCurrency(product.TotalAmount);
    return `
      <tr>
        <td class="product-name">${productName}</td>
        <td class="bf">${bf}</td>
        <td class="mkt">${mkt}</td>
        <td class="total">${total}</td>
      </tr>
    `;
  }).join('');

  // Yemek platformlarından gelen indirim kalemleri 80mm fişte kalabalık
  // yapmasın diye platform bazında TEK satıra toplanır. Asıl tablo görünümü
  // (UI listesi) detaylı kalmaya devam eder — bu sadece print çıktısı içindir.
  // Platform tespiti: "YEMEKSEPETI", "GETIR/Getir", "TRENDYOL/Trendyol",
  // "MIGROS/Migros" ile başlayan tüm varyantlar. Bilinmeyenler kendi isimleriyle
  // tek tek bastırılır (geriye dönük uyumluluk).
  const detectDiscountPlatform = (name: string): string | null => {
    const n = (name || '').trim().toUpperCase();
    if (n.startsWith('YEMEKSEPETI') || n.startsWith('YEMEKSEPETİ') || n.startsWith('YEMEK SEPETI') || n.startsWith('YEMEK SEPETİ')) {
      return 'YEMEKSEPETİ';
    }
    if (n.startsWith('GETIR') || n.startsWith('GETİR')) {
      return 'GETİR';
    }
    if (n.startsWith('TRENDYOL')) {
      return 'TRENDYOL';
    }
    if (n.startsWith('MIGROS') || n.startsWith('MİGROS')) {
      return 'MİGROS';
    }
    return null;
  };

  const platformAggregates = new Map<string, { totalQty: number; totalAmount: number }>();
  const unknownDiscounts: typeof discountItems = [];

  for (const d of discountItems) {
    const platform = detectDiscountPlatform(d.ProductName);
    if (platform) {
      const existing = platformAggregates.get(platform) ?? { totalQty: 0, totalAmount: 0 };
      existing.totalQty += d.TotalQuantity || 0;
      existing.totalAmount += d.TotalAmount || 0;
      platformAggregates.set(platform, existing);
    } else {
      unknownDiscounts.push(d);
    }
  }

  // Platform sırası tutarlı olsun diye sabit sırayla render et.
  const PLATFORM_ORDER = ['YEMEKSEPETİ', 'GETİR', 'TRENDYOL', 'MİGROS'];
  const aggregatedRows = PLATFORM_ORDER
    .filter(p => platformAggregates.has(p))
    .map(p => {
      const agg = platformAggregates.get(p)!;
      const label = truncateText(`${p} İNDİRİMİ`, 18);
      return `
          <tr>
            <td class="product-name">${label}</td>
            <td class="bf">—</td>
            <td class="mkt">${agg.totalQty}</td>
            <td class="total">${formatCurrency(agg.totalAmount)}</td>
          </tr>
        `;
    }).join('');

  const unknownRows = unknownDiscounts.map(d => {
    const name = truncateText(d.ProductName, 18);
    const total = formatCurrency(d.TotalAmount);
    return `
          <tr>
            <td class="product-name">${name}</td>
            <td class="bf">${formatCurrency(d.UnitPrice)}</td>
            <td class="mkt">${d.TotalQuantity.toString()}</td>
            <td class="total">${total}</td>
          </tr>
        `;
  }).join('');

  const discountRows = (platformAggregates.size > 0 || unknownDiscounts.length > 0)
    ? `<tr><td colspan="4" style="text-align:center;border-top:1px dashed #000;padding:2px 0;font-size:9px;">--- ${t('endOfDayReport.printReceipt.discounts', 'DISCOUNTS')} ---</td></tr>` +
      aggregatedRows + unknownRows
    : '';

  const productRows = regularRows + discountRows;

  // Ödeme satırlarını oluştur
  let paymentRows = '';
  
  // Nakit (tüm kasalar birleşik)
  if (paymentSummary.cashTotal > 0) {
    paymentRows += `
      <tr>
        <td>${t('endOfDayReport.paymentTypes.cash', 'Cash').toUpperCase()}</td>
        <td class="amount">${formatCurrency(paymentSummary.cashTotal)} ₺</td>
      </tr>
    `;
  }
  
  // POS (tüm POS'lar birleşik)
  if (paymentSummary.posTotal > 0) {
    paymentRows += `
      <tr>
        <td>POS</td>
        <td class="amount">${formatCurrency(paymentSummary.posTotal)} ₺</td>
      </tr>
    `;
  }
  
  // Online platformlar (ayrı ayrı)
  paymentSummary.onlinePayments.forEach(payment => {
    paymentRows += `
      <tr>
        <td>${payment.name}</td>
        <td class="amount">${formatCurrency(payment.amount)} ₺</td>
      </tr>
    `;
  });
  
  // Diğer ödemeler (ayrı ayrı)
  paymentSummary.otherPayments.forEach(payment => {
    paymentRows += `
      <tr>
        <td>${payment.name}</td>
        <td class="amount">${formatCurrency(payment.amount)} ₺</td>
      </tr>
    `;
  });

  // Ödenmemiş platform siparişleri (Bilinmiyor)
  if (unpaidPlatformOrders) {
    const emptyPlatform = { count: 0, total: 0 };
    const platforms = [
      { name: 'PAKET', data: unpaidPlatformOrders.package || emptyPlatform },
      { name: 'GETİR', data: unpaidPlatformOrders.getir || emptyPlatform },
      { name: 'TRENDYOL', data: unpaidPlatformOrders.trendyol || emptyPlatform },
      { name: 'Y.SEPETİ', data: unpaidPlatformOrders.yemeksepeti || emptyPlatform },
      { name: 'MİGROS', data: unpaidPlatformOrders.migros || emptyPlatform },
    ];
    platforms.forEach(p => {
      if (p.data.count > 0) {
        paymentRows += `
          <tr>
            <td>${p.name} ${t('endOfDayReport.printReceipt.unknown', 'UNKNOWN')}</td>
            <td class="amount">${formatCurrency(p.data.total)} ₺</td>
          </tr>
        `;
      }
    });
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${t('endOfDayReport.title', 'End of Day Report')}</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        @page {
          size: 80mm auto;
          margin: 0;
        }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 11px;
          line-height: 1.5;
          width: 80mm;
          padding: 4mm;
          background: white;
          color: #000;
          font-weight: 500;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .header {
          text-align: center;
          border-bottom: 2px dashed #000;
          padding-bottom: 10px;
          margin-bottom: 10px;
        }
        
        .title {
          font-size: 15px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #000;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          color: #000;
          font-weight: 500;
        }
        
        .info-label {
          font-weight: 600;
          color: #000;
        }
        
        .section {
          margin: 10px 0;
          border-bottom: 2px dashed #000;
          padding-bottom: 10px;
        }
        
        .section-title {
          text-align: center;
          font-weight: 700;
          font-size: 12px;
          margin-bottom: 8px;
          text-decoration: underline;
          color: #000;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        /* Ürün tablosu */
        .product-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
          color: #000;
        }
        
        .product-table th {
          text-align: left;
          border-bottom: 2px solid #000;
          padding: 4px 2px;
          font-weight: 700;
          color: #000;
          font-size: 10px;
        }
        
        .product-table td {
          padding: 4px 2px;
          vertical-align: top;
          word-wrap: break-word;
          color: #000;
          font-weight: 500;
        }
        
        .product-table th:nth-child(1),
        .product-table td.product-name {
          width: 38%;
        }
        
        .product-table th:nth-child(2),
        .product-table td.bf {
          width: 22%;
          text-align: right;
        }
        
        .product-table th:nth-child(3),
        .product-table td.mkt {
          width: 12%;
          text-align: center;
        }
        
        .product-table th:nth-child(4),
        .product-table td.total {
          width: 28%;
          text-align: right;
        }
        
        /* Özet satırları */
        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 5px 0;
          font-weight: 600;
          color: #000;
          font-size: 11px;
        }
        
        .summary-row.highlight {
          font-size: 13px;
          border-top: 2px solid #000;
          margin-top: 6px;
          padding-top: 8px;
          color: #000;
          font-weight: 700;
        }
        
        .summary-row.cancelled {
          color: #000;
        }
        
        /* Ödeme tablosu */
        .payment-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }
        
        .payment-table td {
          padding: 4px 0;
          color: #000;
          font-weight: 500;
        }
        
        .payment-table td.amount {
          text-align: right;
          font-weight: 600;
          color: #000;
        }
        
        .footer {
          text-align: center;
          margin-top: 12px;
          font-size: 10px;
          color: #333;
          font-weight: 500;
        }
        
        @media print {
          body {
            width: 80mm;
          }
          
          @page {
            size: 80mm auto;
            margin: 0;
          }
        }
      </style>
    </head>
    <body>
      <!-- BAŞLIK -->
      <div class="header">
        <div class="title">${t('endOfDayReport.printReceipt.cashReport', 'CASH REPORT')}</div>
      </div>
      
      <!-- BİLGİ BÖLÜMÜ -->
      <div class="section">
        <div class="info-row">
          <span class="info-label">${t('endOfDayReport.printReceipt.branch', 'Branch')}:</span>
          <span>${branchName || t('endOfDayReport.printReceipt.allBranches', 'All Branches')}</span>
        </div>
        <div class="info-row">
          <span class="info-label">${t('endOfDayReport.printReceipt.cashier', 'Cashier')}:</span>
          <span>${cashierNames.length > 0 ? cashierNames.join(', ') : '-'}</span>
        </div>
        <div class="info-row">
          <span class="info-label">${t('endOfDayReport.printReceipt.reportDate', 'Report Date')}:</span>
          <span></span>
        </div>
        <div style="font-size: 10px; margin-left: 10px;">
          ${formatDateTime(reportStartDate)} - ${formatDateTime(reportEndDate)}
        </div>
        <div class="info-row" style="margin-top: 4px;">
          <span class="info-label">${t('endOfDayReport.printReceipt.printDate', 'Print Date')}:</span>
          <span>${formatDateTime(printDate)}</span>
        </div>
      </div>
      
      <!-- ÜRÜN SATIŞ ÖZETİ -->
      <div class="section">
        <div class="section-title">${t('endOfDayReport.printReceipt.productSalesSummary', 'PRODUCT SALES SUMMARY')}</div>
        <table class="product-table">
          <thead>
            <tr>
              <th>${t('endOfDayReport.printReceipt.product', 'Product')}</th>
              <th>${t('endOfDayReport.printReceipt.unitPriceShort', 'UP')}</th>
              <th>${t('endOfDayReport.printReceipt.quantityShort', 'QTY')}</th>
              <th>${t('endOfDayReport.printReceipt.totalShort', 'TOT.')}</th>
            </tr>
          </thead>
          <tbody>
            ${productRows || `<tr><td colspan="4" style="text-align:center;">${t('endOfDayReport.printReceipt.noProductFound', 'No product found')}</td></tr>`}
          </tbody>
        </table>
      </div>
      
      <!-- CİRO -->
      <div class="section">
        <div class="summary-row highlight">
          <span>${t('endOfDayReport.printReceipt.revenue', 'REVENUE')}:</span>
          <span>${formatCurrency(totalRevenue)} ₺</span>
        </div>
      </div>
      
      <!-- MÜŞTERİ SAYISI -->
      <div class="section">
        <div class="summary-row">
          <span>${t('endOfDayReport.printReceipt.customerCount', 'CUSTOMER COUNT')}:</span>
          <span>${customerCount}</span>
        </div>
      </div>
      
      <!-- ÖDEME DETAYLARI -->
      <div class="section">
        <div class="section-title">${t('endOfDayReport.printReceipt.paymentDetails', 'PAYMENT DETAILS')}</div>
        <table class="payment-table">
          <tbody>
            ${paymentRows || `<tr><td colspan="2" style="text-align:center;">${t('endOfDayReport.printReceipt.noPaymentFound', 'No payment found')}</td></tr>`}
          </tbody>
        </table>
        
        <div class="summary-row highlight">
          <span>${t('endOfDayReport.printReceipt.grandTotal', 'GRAND TOTAL')}:</span>
          <span>${formatCurrency(grandTotal)} ₺</span>
        </div>
        
        ${cancelledTotal > 0 ? (() => {
          const orders = data.cancelledOrders || [];
          const restCount = orders.filter(o => o.source === 'restaurant').length;
          const qsCount = orders.filter(o => o.source === 'quickSale').length;
          const label = restCount > 0 && qsCount === 0 ? `${t('endOfDayReport.printReceipt.restaurantCancelled', 'Restaurant Cancelled')}:` 
            : qsCount > 0 && restCount === 0 ? `${t('endOfDayReport.printReceipt.quickSaleCancelled', 'Quick Sale Cancelled')}:` 
            : `${t('endOfDayReport.printReceipt.cancelledAmount', 'Cancelled Amount')}:`;
          return `
          <div class="summary-row cancelled">
            <span>${label}</span>
            <span>${formatCurrency(cancelledTotal)} ₺</span>
          </div>`;
        })() : ''}
        ${(data.posRefundTotal || 0) > 0 ? `
          <div class="summary-row cancelled">
            <span>${t('endOfDayReport.printReceipt.posRefund', 'POS REFUND')} (${data.posRefundCount || 0} ${t('endOfDayReport.printReceipt.items', 'items')}):</span>
            <span>-${formatCurrency(data.posRefundTotal || 0)} ₺</span>
          </div>
          <div class="summary-row" style="font-weight:bold;border-top:1px dashed #000;padding-top:4px;">
            <span>${t('endOfDayReport.printReceipt.netTotal', 'NET TOTAL')}:</span>
            <span>${formatCurrency(grandTotal - (data.posRefundTotal || 0))} ₺</span>
          </div>
        ` : ''}
        ${(data.prepaidTotal || 0) > 0 ? `
          <div class="summary-row" style="font-style:italic;">
            <span>${t('endOfDayReport.paymentTypes.prepaid', 'Prepaid')} (${data.prepaidCount || 0} ${t('endOfDayReport.printReceipt.items', 'items')}):</span>
            <span>${formatCurrency(data.prepaidTotal || 0)} ₺</span>
          </div>
        ` : ''}
      </div>
      
      ${platformOrders && (platformOrders.getir.count > 0 || platformOrders.trendyol.count > 0 || platformOrders.yemeksepeti.count > 0 || platformOrders.migros.count > 0) ? (() => {
        // Platform fiş satırı: Platform (n sipariş) | Brüt | Net
        // Brüt = indirim öncesi (originalTotal) — EndOfDayReport'taki "İndirimsiz Toplam"
        //        kolonuyla aynı kaynak. Eski kayıtta dolu değilse otomatik net'e düşer.
        // Net  = indirim sonrası ödenen (grandTotal).
        const renderRow = (label: string, p: { count: number; total: number; originalTotal: number }) => `
          <tr>
            <td>${label} (${p.count} ${t('endOfDayReport.printReceipt.orders', 'orders')})</td>
            <td style="text-align:right;font-weight:600;">${formatCurrency(p.originalTotal)} ₺</td>
            <td class="amount">${formatCurrency(p.total)} ₺</td>
          </tr>
        `;
        // Toplam satırı için brüt ve net'i ayrı topla.
        let totalGross = 0, totalNet = 0;
        const all = [
          { label: 'GETİR', data: platformOrders.getir },
          { label: 'TRENDYOL', data: platformOrders.trendyol },
          { label: 'YEMEKSEPETİ', data: platformOrders.yemeksepeti },
          { label: 'MİGROS', data: platformOrders.migros },
        ];
        all.forEach(p => {
          if (p.data.count > 0) {
            totalGross += p.data.originalTotal || 0;
            totalNet += p.data.total || 0;
          }
        });
        return `
      <div class="section">
        <div class="section-title">${t('endOfDayReport.printReceipt.foodSiteDetails', 'FOOD DELIVERY DETAILS')}</div>
        <table class="payment-table" style="font-size:10px;">
          <thead>
            <tr style="border-bottom:1px solid #000;">
              <th style="text-align:left;font-weight:700;padding:2px 0;">${t('endOfDayReport.printReceipt.platform', 'Platform')}</th>
              <th style="text-align:right;font-weight:700;padding:2px 0;">${t('endOfDayReport.printReceipt.gross', 'Brüt')}</th>
              <th style="text-align:right;font-weight:700;padding:2px 0;">${t('endOfDayReport.printReceipt.net', 'Net')}</th>
            </tr>
          </thead>
          <tbody>
            ${all.filter(p => p.data.count > 0).map(p => renderRow(p.label, p.data)).join('')}
            <tr>
              <td style="font-weight:bold;border-top:1px dashed #000;padding-top:3px;">${t('endOfDayReport.printReceipt.total', 'TOTAL')}:</td>
              <td style="text-align:right;font-weight:bold;border-top:1px dashed #000;padding-top:3px;">${formatCurrency(totalGross)} ₺</td>
              <td class="amount" style="font-weight:bold;border-top:1px dashed #000;padding-top:3px;">${formatCurrency(totalNet)} ₺</td>
            </tr>
          </tbody>
        </table>
      </div>
        `;
      })() : ''}
      
      ${otherMethodTotals && otherMethodTotals.length > 0 ? `
      <div class="section">
        <div class="section-title">${t('endOfDayReport.printReceipt.otherPaymentMethods', 'OTHER PAYMENT METHODS')}</div>
        <table class="payment-table">
          <tbody>
            ${otherMethodTotals.map(item => `
              <tr>
                <td>${truncateText(item.name, 24)}</td>
                <td class="amount">${formatCurrency(item.amount)} ₺</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ` : ''}
      
      ${(() => {
        const orders = data.cancelledOrders || [];
        if (orders.length === 0) return '';
        const totalCancelled = orders.reduce((s, o) => s + o.amount, 0);
        const restCount = orders.filter(o => o.source === 'restaurant').length;
        const qsCount = orders.filter(o => o.source === 'quickSale').length;
        const sectionTitle = restCount > 0 && qsCount === 0 ? `${t('endOfDayReport.printReceipt.cancelledRestaurantReceipts', 'CANCELLED RESTAURANT RECEIPTS')} (${orders.length} ${t('endOfDayReport.printReceipt.items', 'items')})`
          : qsCount > 0 && restCount === 0 ? `${t('endOfDayReport.printReceipt.cancelledQuickSaleReceipts', 'CANCELLED QUICK SALE RECEIPTS')} (${orders.length} ${t('endOfDayReport.printReceipt.items', 'items')})`
          : `${t('endOfDayReport.printReceipt.cancelledReceipts', 'CANCELLED RECEIPTS')} (${orders.length} ${t('endOfDayReport.printReceipt.items', 'items')})`;
        return `
      <div class="section">
        <div class="section-title">${sectionTitle}</div>
        <table class="product-table">
          <thead>
            <tr>
              <th>${t('endOfDayReport.printReceipt.tableOrReceipt', 'Table / Receipt')}</th>
              <th>${t('endOfDayReport.printReceipt.waiter', 'Waiter')}</th>
              <th style="text-align:right">${t('endOfDayReport.printReceipt.amount', 'Amount')}</th>
            </tr>
          </thead>
          <tbody>
            ${orders.map(o => `
              <tr>
                <td>${truncateText(o.tableName || '-', 14)}</td>
                <td>${truncateText(o.waiterName || '-', 10)}</td>
                <td style="text-align:right">${formatCurrency(o.amount)} ₺</td>
              </tr>
            `).join('')}
            <tr>
              <td colspan="2" style="font-weight:bold;border-top:1px dashed #000;padding-top:3px;">${t('endOfDayReport.printReceipt.cancelledTotal', 'CANCELLED TOTAL')}:</td>
              <td style="text-align:right;font-weight:bold;border-top:1px dashed #000;padding-top:3px;">${formatCurrency(totalCancelled)} ₺</td>
            </tr>
          </tbody>
        </table>
      </div>`;
      })()}
      
      ${(() => {
        if (!unpaidPlatformOrders) return '';
        const pkg = unpaidPlatformOrders.package || { count: 0, total: 0 };
        const gtr = unpaidPlatformOrders.getir || { count: 0, total: 0 };
        const trd = unpaidPlatformOrders.trendyol || { count: 0, total: 0 };
        const ys = unpaidPlatformOrders.yemeksepeti || { count: 0, total: 0 };
        const mgr = unpaidPlatformOrders.migros || { count: 0, total: 0 };
        if (pkg.count === 0 && gtr.count === 0 && trd.count === 0 && ys.count === 0 && mgr.count === 0) return '';
        return `
      <div class="section">
        <div class="section-title">${t('endOfDayReport.printReceipt.unpaidOrders', 'UNPAID ORDERS')}</div>
        <table class="payment-table">
          <tbody>
            ${pkg.count > 0 ? `<tr><td>${t('endOfDayReport.printReceipt.packagePhone', 'PACKAGE/PHONE')} (${pkg.count} ${t('endOfDayReport.printReceipt.orders', 'orders')})</td><td class="amount">${formatCurrency(pkg.total)} ₺</td></tr>` : ''}
            ${gtr.count > 0 ? `<tr><td>GETİR (${gtr.count} ${t('endOfDayReport.printReceipt.orders', 'orders')})</td><td class="amount">${formatCurrency(gtr.total)} ₺</td></tr>` : ''}
            ${trd.count > 0 ? `<tr><td>TRENDYOL (${trd.count} ${t('endOfDayReport.printReceipt.orders', 'orders')})</td><td class="amount">${formatCurrency(trd.total)} ₺</td></tr>` : ''}
            ${ys.count > 0 ? `<tr><td>YEMEKSEPETİ (${ys.count} ${t('endOfDayReport.printReceipt.orders', 'orders')})</td><td class="amount">${formatCurrency(ys.total)} ₺</td></tr>` : ''}
            ${mgr.count > 0 ? `<tr><td>MİGROS (${mgr.count} ${t('endOfDayReport.printReceipt.orders', 'orders')})</td><td class="amount">${formatCurrency(mgr.total)} ₺</td></tr>` : ''}
            <tr>
              <td style="font-weight:bold;border-top:1px dashed #000;padding-top:3px;">${t('endOfDayReport.printReceipt.totalUnpaid', 'TOTAL UNPAID')}:</td>
              <td class="amount" style="font-weight:bold;border-top:1px dashed #000;padding-top:3px;">${formatCurrency(
                (pkg.total || 0) + (gtr.total || 0) + (trd.total || 0) + (ys.total || 0) + (mgr.total || 0)
              )} ₺</td>
            </tr>
          </tbody>
        </table>
      </div>`;
      })()}
      
    </body>
    </html>
  `;
};

// Yazdırma fonksiyonu
export const printEndOfDayReceipt = (data: EndOfDayPrintData): void => {
  const html = generateEndOfDayReceiptHTML(data);
  
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const windowWidth = 450;
  const windowHeight = 700;
  const left = (screenWidth - windowWidth) / 2;
  const top = (screenHeight - windowHeight) / 2;
  
  const printWindow = window.open('', '_blank', `width=${windowWidth},height=${windowHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  } else {
    console.error('Yazdırma penceresi açılamadı');
    alert(i18n.t('endOfDayReport.printReceipt.popupBlocked', { ns: 'salesSystem', defaultValue: 'Print window could not be opened. Please check your popup blocker.' }));
  }
};

export default {
  generateEndOfDayReceiptHTML,
  printEndOfDayReceipt,
};
